import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Filter,
  Download,
  Trash2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Database,
  FileText,
  Zap,
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square,
  Eye,
  Clock,
  RefreshCw
} from 'lucide-react';
import { getPokemonImageUrl } from '../../utils/imageUtils';

import { getApiBaseUrl } from '../../utils/apiUrl';
const API_URL = getApiBaseUrl();
const MAX_ITEMS_PER_PAGE = 100;

const DataManagementCenter = () => {
  // Data states
  const [pokemonData, setPokemonData] = useState([]);
  const [importHistory, setImportHistory] = useState([]);
  const [countersSummary, setCountersSummary] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [notification, setNotificationState] = useState(null);
  
  // Filter & Search states
  const [dataType, setDataType] = useState('all'); // 'all', 'pokemon', 'imports', 'counters'
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'valid', 'error', 'incomplete'
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  
  // Bulk selection states
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);
  
  // View states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState([]);

  const getAuthToken = () => {
    return localStorage.getItem('adminToken') || '';
  };

  const showNotification = (message, type = 'success') => {
    setNotificationState({ message, type });
    setTimeout(() => setNotificationState(null), 5000);
  };

  // Status helper functions (must be defined before useMemo hooks)
  const getPokemonStatus = (pokemon) => {
    // Detailed validation: check for missing required fields
    const missingFields = [];
    
    // Required fields
    if (!pokemon.Pokemon_Name_EN && !pokemon.name_en && !pokemon.name) {
      missingFields.push('Name');
    }
    if (!pokemon.Pokemon_ID && !pokemon.id) {
      missingFields.push('ID');
    }
    if (pokemon.Attack === undefined || pokemon.Attack === null || pokemon.Attack === '') {
      missingFields.push('Attack');
    }
    if (pokemon.Defense === undefined || pokemon.Defense === null || pokemon.Defense === '') {
      missingFields.push('Defense');
    }
    if (pokemon.HP === undefined && pokemon.hp === undefined && pokemon.Stamina === undefined && pokemon.stamina === undefined) {
      missingFields.push('HP/Stamina');
    }
    if (!pokemon.Type1 && !pokemon.type1) {
      missingFields.push('Type1');
    }
    if (!pokemon.Max_CP_Lv50 && pokemon.max_cp_level_50 === undefined && pokemon.Max_CP === undefined && pokemon.max_cp === undefined) {
      missingFields.push('Max CP');
    }
    
    // Store missing fields in pokemon object for display
    if (missingFields.length > 0) {
      pokemon._missingFields = missingFields;
      return 'warning';
    }
    
    return 'valid';
  };
  
  const getPokemonMissingFields = (pokemon) => {
    return pokemon._missingFields || [];
  };

  const getImportStatus = (importLog) => {
    const status = importLog.import_status || importLog.status || 'unknown';
    if (status === 'completed') return 'valid';
    if (status === 'failed') return 'error';
    if (status === 'processing') return 'warning';
    return 'warning';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'valid':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      valid: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
    };
    return badges[status] || badges.warning;
  };

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
      setCurrentPage(1); // Reset to first page on search
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch Pokemon data
  const fetchPokemon = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: Math.min(itemsPerPage, MAX_ITEMS_PER_PAGE).toString(),
        search: debouncedSearchQuery,
        sortBy: 'id',
        sortOrder: 'asc'
      });

      const response = await fetch(`${API_URL}/api/pokemon?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const data = await response.json();
      if (data.success) {
        const pokemon = data.data.pokemon || data.data || [];
        setPokemonData(pokemon);
        setTotalItems(data.data.pagination?.totalItems || pokemon.length);
      }
    } catch (error) {
      console.error('Error fetching Pokemon:', error);
    }
  }, [currentPage, itemsPerPage, debouncedSearchQuery]);

  // Fetch import history
  const fetchImports = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/pokemon-admin/import/history`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const data = await response.json();
      let history = [];
      if (data.success) {
        history = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
      } else {
        history = Array.isArray(data) ? data : [];
      }
      setImportHistory(history);
    } catch (error) {
      console.error('Error fetching import history:', error);
      setImportHistory([]);
    }
  }, []);

  // Fetch counters summary
  const fetchCountersSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin-stats/overview`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`
        }
      });

      const data = await response.json();
      if (data.success && data.data.counters) {
        setCountersSummary(data.data.counters);
      }
    } catch (error) {
      console.error('Error fetching counters summary:', error);
    }
  }, []);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dataType === 'all' || dataType === 'pokemon' ? fetchPokemon() : Promise.resolve(),
          dataType === 'all' || dataType === 'imports' ? fetchImports() : Promise.resolve(),
          dataType === 'all' || dataType === 'counters' ? fetchCountersSummary() : Promise.resolve()
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [dataType, fetchPokemon, fetchImports, fetchCountersSummary]);

  // Filter and search combined data
  const filteredData = useMemo(() => {
    let data = [];

    // Collect data based on dataType filter
    if (dataType === 'all' || dataType === 'pokemon') {
      const pokemonItems = (Array.isArray(pokemonData) ? pokemonData : []).map(p => {
        const status = getPokemonStatus(p);
        return {
          id: p.Pokemon_ID || p.id,
          type: 'pokemon',
          name: p.Pokemon_Name_EN || p.name_en || p.name,
          nameTh: p.Pokemon_Name_TH || p.name_th,
          status: status,
          missingFields: getPokemonMissingFields(p),
          lastUpdated: null, // Pokemon doesn't have updated_at in current API
          source: 'manual', // Assume manual unless we track this
          data: p
        };
      });
      data.push(...pokemonItems);
    }

    if (dataType === 'all' || dataType === 'imports') {
      const importItems = (Array.isArray(importHistory) ? importHistory : []).map(imp => ({
        id: `import-${imp.id}`,
        type: 'import',
        name: imp.file_name || 'Unknown',
        nameTh: imp.file_name || 'Unknown',
        status: getImportStatus(imp),
        lastUpdated: imp.completed_at || imp.started_at || imp.created_at,
        source: 'csv_import',
        data: imp
      }));
      data.push(...importItems);
    }

    if (dataType === 'all' || dataType === 'counters') {
      // Counters are read-only summary, show as single item
      if (countersSummary) {
        data.push({
          id: 'counters-summary',
          type: 'counters',
          name: 'Counters Summary',
          nameTh: 'สรุป Counters',
          status: 'valid',
          lastUpdated: null,
          source: 'database',
          data: countersSummary
        });
      }
    }

    // Apply search filter
    if (debouncedSearchQuery) {
      const query = debouncedSearchQuery.toLowerCase();
      data = data.filter(item => {
        if (item.type === 'pokemon') {
          return (
            item.name.toLowerCase().includes(query) ||
            item.nameTh.toLowerCase().includes(query) ||
            item.id.toString().includes(query)
          );
        } else if (item.type === 'import') {
          return (
            item.name.toLowerCase().includes(query) ||
            item.id.toString().includes(query) ||
            (item.data.import_status || item.data.status || '').toLowerCase().includes(query)
          );
        }
        return true;
      });
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      data = data.filter(item => {
        if (statusFilter === 'valid') return item.status === 'valid';
        if (statusFilter === 'error') return item.status === 'error';
        if (statusFilter === 'incomplete') return item.status === 'warning';
        return true;
      });
    }

    return data;
  }, [pokemonData, importHistory, countersSummary, dataType, debouncedSearchQuery, statusFilter]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredData.slice(start, end);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedItems(new Set());
      setSelectAll(false);
    } else {
      const allIds = new Set(paginatedData.map(item => item.id));
      setSelectedItems(allIds);
      setSelectAll(true);
    }
  };

  const handleSelectItem = (id) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedItems(newSelected);
    setSelectAll(newSelected.size === paginatedData.length);
  };

  // Bulk operations
  const handleBulkDelete = () => {
    const targets = Array.from(selectedItems).map(id => {
      const item = filteredData.find(d => d.id === id);
      return item;
    }).filter(Boolean);

    // Filter out counters (read-only)
    const deletableTargets = targets.filter(t => t.type !== 'counters');
    
    if (deletableTargets.length === 0) {
      showNotification('No deletable items selected', 'error');
      return;
    }

    setDeleteTargets(deletableTargets);
    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    try {
      const pokemonIds = deleteTargets.filter(t => t.type === 'pokemon').map(t => t.id);
      
      // Delete Pokemon (imports are read-only logs)
      for (const id of pokemonIds) {
        const response = await fetch(`${API_URL}/api/pokemon-admin/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${getAuthToken()}`
          }
        });

        if (!response.ok) {
          throw new Error(`Failed to delete ${id}`);
        }
      }

      showNotification(`Successfully deleted ${pokemonIds.length} item(s)`, 'success');
      setSelectedItems(new Set());
      setSelectAll(false);
      setShowDeleteConfirm(false);
      setDeleteTargets([]);
      
      // Refresh data
      if (dataType === 'all' || dataType === 'pokemon') {
        await fetchPokemon();
      }
    } catch (error) {
      console.error('Error deleting items:', error);
      showNotification('Failed to delete items', 'error');
    }
  };

  const handleBulkExport = () => {
    const targets = Array.from(selectedItems).map(id => {
      return filteredData.find(d => d.id === id);
    }).filter(Boolean);

    if (targets.length === 0) {
      showNotification('No items selected', 'error');
      return;
    }

    // Export as JSON
    const exportData = targets.map(t => t.data);
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `export-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);

    showNotification(`Exported ${targets.length} item(s)`, 'success');
  };

  return (
    <div className="space-y-6">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg flex items-center space-x-3 ${
              notification.type === 'success'
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-red-100 text-red-800 border border-red-200'
            }`}
          >
            {notification.type === 'success' ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <XCircle className="w-5 h-5" />
            )}
            <span>{notification.message}</span>
            <button onClick={() => setNotificationState(null)} className="ml-2">
              <XCircle className="w-4 h-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Data Management Center</h1>
          <p className="text-gray-600 dark:text-gray-400">จัดการข้อมูลทั้งหมดในที่เดียว: Pokemon, Import History, และ Counters</p>
        </div>
        <button
          onClick={() => {
            if (dataType === 'all' || dataType === 'pokemon') fetchPokemon();
            if (dataType === 'all' || dataType === 'imports') fetchImports();
            if (dataType === 'all' || dataType === 'counters') fetchCountersSummary();
          }}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Refresh</span>
        </button>
      </div>

      {/* Statistics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Pokemon</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Array.isArray(pokemonData) ? pokemonData.length : 0}
              </p>
            </div>
            <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <Zap className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {filteredData.filter(d => d.type === 'pokemon').length} shown
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Import History</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {Array.isArray(importHistory) ? importHistory.length : 0}
              </p>
            </div>
            <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
              <FileText className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {importHistory.filter(imp => (imp.import_status || imp.status) === 'failed').length} failed
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Data Issues</p>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                {filteredData.filter(d => d.status === 'error' || d.status === 'warning').length}
              </p>
            </div>
            <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-lg">
              <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Needs attention
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Selected Items</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {selectedItems.size}
              </p>
            </div>
            <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <CheckSquare className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Ready for bulk action
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <button
            onClick={() => {
              setDataType('pokemon');
              setStatusFilter('incomplete');
              setSearchQuery('');
            }}
            className="flex flex-col items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors border border-yellow-200 dark:border-yellow-800"
          >
            <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mb-2" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">ข้อมูลไม่ครบ</span>
          </button>
          <button
            onClick={() => {
              setDataType('pokemon');
              setStatusFilter('error');
              setSearchQuery('');
            }}
            className="flex flex-col items-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors border border-red-200 dark:border-red-800"
          >
            <XCircle className="w-6 h-6 text-red-600 dark:text-red-400 mb-2" />
            <span className="text-sm font-medium text-red-800 dark:text-red-300">Find Errors</span>
          </button>
          <button
            onClick={() => {
              setDataType('imports');
              setStatusFilter('error');
              setSearchQuery('');
            }}
            className="flex flex-col items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors border border-yellow-200 dark:border-yellow-800"
          >
            <AlertCircle className="w-6 h-6 text-yellow-600 dark:text-yellow-400 mb-2" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-300">Failed Imports</span>
          </button>
          <button
            onClick={() => {
              setDataType('all');
              setStatusFilter('all');
              setSearchQuery('');
              const allIds = new Set(filteredData.map(d => d.id));
              setSelectedItems(allIds);
              setSelectAll(true);
            }}
            className="flex flex-col items-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors border border-blue-200 dark:border-blue-800"
          >
            <CheckSquare className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" />
            <span className="text-sm font-medium text-blue-800 dark:text-blue-300">Select All</span>
          </button>
          <button
            onClick={handleBulkExport}
            disabled={selectedItems.size === 0}
            className="flex flex-col items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors border border-green-200 dark:border-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-6 h-6 text-green-600 dark:text-green-400 mb-2" />
            <span className="text-sm font-medium text-green-800 dark:text-green-300">Export Selected</span>
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, ID, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Data Type Filter */}
          <div>
            <select
              value={dataType}
              onChange={(e) => {
                setDataType(e.target.value);
                setCurrentPage(1);
                setSelectedItems(new Set());
                setSelectAll(false);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Data Types</option>
              <option value="pokemon">Pokemon</option>
              <option value="imports">Import History</option>
              <option value="counters">Counters (Read-only)</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="valid">Valid (ข้อมูลครบ)</option>
              <option value="error">Errors (มีข้อผิดพลาด)</option>
              <option value="incomplete">Incomplete (ข้อมูลไม่ครบ)</option>
            </select>
          </div>
        </div>

        {/* Bulk Actions */}
        {selectedItems.size > 0 && (
          <div className="mt-4 flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <span className="text-sm font-medium text-blue-900 dark:text-blue-200">
              {selectedItems.size} item(s) selected
            </span>
            <div className="flex space-x-2">
              <button
                onClick={handleBulkExport}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span>Export</span>
              </button>
              {Array.from(selectedItems).some(id => {
                const item = filteredData.find(d => d.id === id);
                return item && item.type !== 'counters';
              }) && (
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Data Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : paginatedData.length === 0 ? (
          <div className="text-center py-16">
            <Database className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600 dark:text-gray-400 text-lg">No data found</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
              {searchQuery ? 'Try adjusting your search or filters' : 'No data available for the selected filters'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <button
                        onClick={handleSelectAll}
                        className="flex items-center"
                      >
                        {selectAll ? (
                          <CheckSquare className="w-5 h-5 text-blue-600" />
                        ) : (
                          <Square className="w-5 h-5 text-gray-400" />
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Source</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Last Updated</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900 dark:text-white">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {paginatedData.map((item) => (
                    <tr
                      key={item.id}
                      className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                    >
                      <td className="px-4 py-3">
                        {item.type !== 'counters' && (
                          <button
                            onClick={() => handleSelectItem(item.id)}
                            className="flex items-center"
                          >
                            {selectedItems.has(item.id) ? (
                              <CheckSquare className="w-5 h-5 text-blue-600" />
                            ) : (
                              <Square className="w-5 h-5 text-gray-400" />
                            )}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          item.type === 'pokemon' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                          item.type === 'import' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400' :
                          'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
                        }`}>
                          {item.type === 'pokemon' && <Zap className="w-3 h-3 mr-1" />}
                          {item.type === 'import' && <FileText className="w-3 h-3 mr-1" />}
                          {item.type === 'counters' && <Database className="w-3 h-3 mr-1" />}
                          {item.type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          {item.type === 'pokemon' && (
                            <img
                              src={getPokemonImageUrl(item.id)}
                              alt={item.name}
                              className="w-10 h-10 object-contain"
                              onError={(e) => {
                                e.target.src = '/images/pokemon/0000.webp';
                              }}
                            />
                          )}
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">{item.name}</div>
                            {item.nameTh && item.nameTh !== item.name && (
                              <div className="text-sm text-gray-500 dark:text-gray-400">{item.nameTh}</div>
                            )}
                            {item.type === 'pokemon' && (
                              <div className="text-xs text-gray-400">ID: {item.id}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col space-y-1">
                          <div className="flex items-center space-x-2">
                            {getStatusIcon(item.status)}
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${getStatusBadge(item.status)}`}>
                              {item.status === 'warning' ? 'ข้อมูลไม่ครบ' : item.status}
                            </span>
                          </div>
                          {item.missingFields && item.missingFields.length > 0 && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              ขาด: {item.missingFields.join(', ')}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {item.source === 'csv_import' ? (
                          <span className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            CSV Import
                          </span>
                        ) : item.source === 'manual' ? (
                          <span className="flex items-center">
                            <Database className="w-4 h-4 mr-1" />
                            Manual
                          </span>
                        ) : (
                          <span className="flex items-center">
                            <Database className="w-4 h-4 mr-1" />
                            Database
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {item.lastUpdated ? (
                          <span className="flex items-center">
                            <Clock className="w-4 h-4 mr-1" />
                            {new Date(item.lastUpdated).toLocaleDateString()}
                          </span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end space-x-2">
                          {item.type === 'pokemon' && (
                            <button
                              onClick={() => window.open(`/pokemon/${item.id}`, '_blank')}
                              className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {item.type === 'import' && item.data.failed_rows > 0 && (
                            <a
                              href={`${API_URL}/api/pokemon-admin/import/errors/${item.data.id}?format=csv`}
                              download
                              className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                              title="Download Error Report"
                            >
                              <Download className="w-4 h-4" />
                            </a>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} items
                  </span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Math.min(parseInt(e.target.value), MAX_ITEMS_PER_PAGE));
                      setCurrentPage(1);
                    }}
                    className="ml-4 px-2 py-1 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowDeleteConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
                Confirm Deletion
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Are you sure you want to delete {deleteTargets.length} item(s)? This action cannot be undone.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteTargets([]);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBulkDelete}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DataManagementCenter;

